import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Truck, Sparkles, IndianRupee, Zap, PackageOpen, Split, Camera, ScanLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CourierSelect from "@/components/CourierSelect";
import RecipientPicker from "@/components/RecipientPicker";
import ClientVendorPicker from "@/components/ClientVendorPicker";
import ParcelTypeSelect from "@/components/ParcelTypeSelect";
import ShippingModeSelect from "@/components/ShippingModeSelect";
import RateSuggestion from "@/components/RateSuggestion";
import PincodeInput from "@/components/PincodeInput";
import BulkUploadButton from "@/components/BulkUploadButton";
import ParcelConditionSection, { DEFAULT_CONDITION } from "@/components/ParcelConditionSection";
import CostCenterSelect from "@/components/CostCenterSelect";
import ItemBreakdownEditor from "@/components/ItemBreakdownEditor";
import ShipmentTypeRadio from "@/components/ShipmentTypeRadio";
import WeightBlock from "@/components/WeightBlock";
import CostBreakdownPanel from "@/components/CostBreakdownPanel";
import LocationPicker from "@/components/LocationPicker";
import BookParcelDialog from "@/components/BookParcelDialog";

const empty = {
  awb_number: "", courier: "", shipping_mode: "Land",
  ship_product: "Surface",
  entry_mode: "Courier", scope: "Domestic", shipment_type: "Official",
  sender_name: "", sender_address: "", sender_phone: "", sender_email: "", source_pincode: "",
  recipient_id: null, recipient_name: "", recipient_address: "", recipient_phone: "", recipient_department: "", recipient_email: "",
  actual_weight_kg: 0.5, length_cm: "", breadth_cm: "", height_cm: "",
  weight_kg: 0.5, dimensions: "", parcel_type: "Document", item_count: 1, notes: "",
  fuel_surcharge: 0,
  destination_city: "", destination_pincode: "",
  destination_location_id: null,
  priority: false, priority_rate: null,
  is_rto: false,
  condition: DEFAULT_CONDITION,
  items: [],
  allow_split_delivery: false,
};

export default function OutwardEntryPage() {
  const [form, setForm] = useState(empty);
  const [urgency, setUrgency] = useState("standard");
  const [rates, setRates] = useState([]);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bookImmediately, setBookImmediately] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [savedParcel, setSavedParcel] = useState(null);
  // Dynamic Rate Matrix — auto-resolved shipment zone (read-only display).
  const [shipmentZone, setShipmentZone] = useState({ zone: "", matched_via: "" });
  const [zoneLoading, setZoneLoading] = useState(false);
  const nav = useNavigate();
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const awbOcrRef = useRef(null);
  const [awbOcrBusy, setAwbOcrBusy] = useState(false);
  const [awbPromptOpen, setAwbPromptOpen] = useState(false);
  const [awbManual, setAwbManual] = useState("");

  // Feb 12, 2026 — snap the courier label and auto-fill AWB (uses shared /ai/scan-label).
  const handleAwbOcr = async (file) => {
    if (!file) return;
    setAwbOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/scan-label", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const x = data.extracted || {};
      const awb = (x.awb_number || x.tracking_number || "").trim();
      if (awb) {
        setForm((s) => ({ ...s, awb_number: awb, courier: s.courier || x.courier || "" }));
        toast.success(`AWB extracted · ${awb}`, {
          description: x.courier ? `Courier · ${x.courier}` : undefined });
        setAwbPromptOpen(false); // dismiss prompt if it was open
      } else {
        toast.error("AWB not found on the label — please type it manually.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR failed");
    } finally {
      setAwbOcrBusy(false);
      if (awbOcrRef.current) awbOcrRef.current.value = "";
    }
  };

  // Ship Product → Rate Matrix Service Type mapping. Ship Product is the ONLY source of the
  // service category on Outward Entry — the legacy 4-card ShippingModeSelect has been removed
  // (Feb 2026). Rate Matrix engine is queried strictly with the mapped service_type so 'Air Doc'
  // never returns Surface prices.
  const SHIP_PRODUCT_TO_SERVICE = {
    "Air Doc": "Air-DOC",
    "Air Non-Doc": "Air-NON-DOC",
    "Surface": "Surface",
    "Cargo": "Surface",  // Cargo shares the Surface matrix until a separate 'Cargo' service is configured.
  };
  const currentServiceType = SHIP_PRODUCT_TO_SERVICE[form.ship_product] || form.ship_product;
  // Reverse map: when the user picks a suggested rate row we translate the matrix
  // service_type back to the Ship Product label so the visible dropdown updates.
  const SERVICE_TO_SHIP_PRODUCT = {
    "Air-DOC": "Air Doc",
    "Air-NON-DOC": "Air Non-Doc",
    "Surface": "Surface",
  };

  const fetchRates = async () => {
    if (!form.destination_pincode || form.destination_pincode.length < 3) return;
    const { data } = await api.get("/rates", { params: {
      weight_kg: form.weight_kg || 0.5,
      destination_pincode: form.destination_pincode,
      source_pincode: form.source_pincode || undefined,
      urgency,
      service_type: currentServiceType,   // strict scope — Air Doc → Air-DOC matrix only
    } });
    setRates(data);
  };

  // Recalculate zone + rates whenever Ship Product / Source PIN / Destination PIN / Weight /
  // Origin Branch changes — per spec §12+ (Feb 2026 tightening).
  useEffect(() => { fetchRates(); /* eslint-disable-next-line */ },
    [form.weight_kg, form.source_pincode, form.destination_pincode, urgency,
     form.ship_product, form.branch_id, form.courier]);

  // Auto-resolve shipment zone (Local / State / Metro / Zone A-D / Remote / North East / ROI).
  // Triggered by change in source pincode, destination pincode, or courier. Read-only display.
  useEffect(() => {
    const src = (form.source_pincode || "").trim();
    const dst = (form.destination_pincode || "").trim();
    if (!form.courier || dst.length < 3) {
      setShipmentZone({ zone: "", matched_via: "" });
      return;
    }
    setZoneLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/rate-matrix/resolve-zone", {
          courier: form.courier, source_pincode: src, destination_pincode: dst,
        });
        setShipmentZone({ zone: data.zone, matched_via: data.matched_via });
      } catch (_) {
        setShipmentZone({ zone: "", matched_via: "" });
      } finally { setZoneLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.courier, form.source_pincode, form.destination_pincode]);

  const askAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/recommend-courier", {
        weight_kg: parseFloat(form.weight_kg) || 0.5,
        destination_city: form.destination_city || "Mumbai",
        parcel_type: form.parcel_type,
        urgency,
      });
      setAi(data);
      if (!form.courier) set("courier", data.recommendation);
    } catch (e) {
      toast.error("AI failed");
    } finally { setAiLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sender_name || !form.recipient_name || !form.destination_city) return toast.error("Fill required fields");
    if (!form.source_pincode || form.source_pincode.length < 6) return toast.error("Source pincode is required (6 digits)");
    if (!form.destination_pincode || form.destination_pincode.length < 6) return toast.error("Destination pincode is required (6 digits)");
    if (form.priority && (!form.courier || form.priority_rate === null || form.priority_rate === "" || Number.isNaN(form.priority_rate))) {
      return toast.error("Priority parcels need a courier and manual rate");
    }
    if (form.scope === "International" && (!form.customs_value_inr || Number(form.customs_value_inr) <= 0)) {
      return toast.error("Customs value (INR) is required for International shipments");
    }
    // Feb 12, 2026 — AWB is mandatory. If empty, open the AWB prompt instead of saving.
    if (!(form.awb_number || "").trim()) {
      setAwbManual("");
      setAwbPromptOpen(true);
      return;
    }
    setSaving(true);
    try {
      // Coerce empty-string numerics to null so Pydantic doesn't reject them
      const numOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));
      const payload = {
        ...form,
        length_cm: numOrNull(form.length_cm),
        breadth_cm: numOrNull(form.breadth_cm),
        height_cm: numOrNull(form.height_cm),
        actual_weight_kg: numOrNull(form.actual_weight_kg),
        weight_kg: numOrNull(form.weight_kg) ?? 0.5,
        customs_value_inr: numOrNull(form.customs_value_inr),
        priority_rate: numOrNull(form.priority_rate),
        // Persist auto-resolved shipment zone alongside the parcel for reporting/billing/analytics.
        resolved_zone: shipmentZone.zone || null,
        resolved_zone_via: shipmentZone.matched_via || null,
      };
      const { data } = await api.post("/parcels/outward", payload);
      toast.success(`Outward booked · Reference ${data.tracking_number}`, {
        description: data.awb_number ? `AWB ${data.awb_number}` : undefined,
      });
      if (bookImmediately) {
        // Keep the user on this page and pop the Booking dialog with the freshly created parcel.
        // If the dialog is closed without submitting, the parcel remains saved with booking_status
        // = "not_booked" and SLA "not started" — user can book later from the Parcel List.
        setSavedParcel(data);
        setBookOpen(true);
      } else {
        nav("/app/parcels?direction=outward");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const best = useMemo(() => rates[0], [rates]);
  // Net = chosen courier's rate (or priority manual rate); falls back to cheapest
  const netAmount = useMemo(() => {
    if (form.priority && form.priority_rate) return Number(form.priority_rate) || 0;
    const chosen = rates.find((r) => r.courier === form.courier);
    return chosen ? Number(chosen.price) || 0 : Number(best?.price) || 0;
  }, [form.courier, form.priority, form.priority_rate, rates, best]);

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><PackageOpen size={12} /> New outward shipment</div>
          <h1 className="text-4xl font-display mt-1">Outward Entry.</h1>
          <p className="text-sm text-muted-foreground mt-1">Compare live rates and let AI recommend the best courier.</p>
        </div>
        <BulkUploadButton direction="outward" testid="outward-bulk-btn" />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-3 border border-border bg-card p-6 space-y-6" data-testid="outward-form">
          <div className="space-y-4">
            <div className="overline">Consignor (Sender · internal employee)</div>
            <RecipientPicker
              value={form.recipient_id ? { id: form.recipient_id, name: form.sender_name, department: form.recipient_department } : null}
              onChange={(r) => setForm((s) => ({
                ...s, recipient_id: r.id, sender_name: r.name,
                recipient_department: r.department || s.recipient_department,
                // Feb 12, 2026 — auto-fill source pincode from the employee's saved pincode.
                source_pincode: r.pincode || s.source_pincode,
              }))}
              testid="out-sender-picker"
              label="Pick employee (consignor) from directory"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Consignor name" required v={form.sender_name} set={(v)=>set("sender_name",v)} t="out-sender" />
              <div>
                <span className="overline">Consignor / Source pincode<span className="text-primary"> *</span></span>
                <PincodeInput
                  value={form.source_pincode}
                  onChange={(v) => set("source_pincode", v)}
                  testid="out-source-pincode"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6 space-y-4">
            <div className="overline">Consignee (Recipient) & Destination</div>
            {/* T6 · pick from Locations master · falls back to manual override fields below */}
            <LocationPicker
              value={form.destination_location_id ? { id: form.destination_location_id, name: form.recipient_name, address: form.recipient_address } : null}
              onChange={(l) => setForm((s) => ({
                ...s,
                destination_location_id: l.id,
                recipient_name: l.type === "person" ? l.name : (l.contact_person || l.name),
                recipient_address: l.address || s.recipient_address,
                recipient_phone: l.phone || s.recipient_phone,
                recipient_email: l.email || s.recipient_email,
              }))}
              testid="out-destination-picker"
              label="Destination from location master"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ClientVendorPicker
                label="Consignee name"
                required
                kind="recipient"
                testid="out-recipient"
                value={form.recipient_name ? { id: null, name: form.recipient_name, phone: form.recipient_phone, email: form.recipient_email, address: form.recipient_address } : null}
                onChange={(r) => setForm((s) => ({
                  ...s,
                  recipient_name: r.name || "",
                  recipient_phone: r.phone || s.recipient_phone,
                  recipient_email: r.email || s.recipient_email,
                  recipient_address: r.address || s.recipient_address,
                }))}
                placeholder="Type or pick from Client/Vendor Master…"
              />
              <F label="Consignee phone" v={form.recipient_phone} set={(v)=>set("recipient_phone",v)} t="out-recipient-phone"/>
            </div>
            <F label="Consignee email (optional)" v={form.recipient_email} set={(v)=>set("recipient_email",v)} t="out-recipient-email" type="email" />
            <F label="Delivery address" v={form.recipient_address} set={(v)=>set("recipient_address",v)} textarea t="out-recipient-addr"/>
            <div className="grid grid-cols-2 gap-4">
              <F label="Destination city" required v={form.destination_city} set={(v)=>set("destination_city",v)} t="out-city"/>
              <div>
                <span className="overline">Pincode</span>
                <PincodeInput
                  value={form.destination_pincode}
                  onChange={(v) => set("destination_pincode", v)}
                  onResolved={(info) => { if (!form.destination_city && info?.city) set("destination_city", info.city); }}
                  testid="out-pincode"
                />
              </div>
            </div>
            {/* Auto-populated Shipment Zone (Dynamic Rate Matrix). Read-only, auto-refreshed. */}
            <div className="border border-primary/30 bg-red-50/40 rounded-md p-3 flex items-center gap-3 flex-wrap"
                 data-testid="out-zone-panel">
              <span className="overline">Shipment Zone</span>
              {shipmentZone.zone ? (
                <>
                  <span className="mono text-sm font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded"
                        data-testid="out-zone-value">{shipmentZone.zone}</span>
                  <span className="text-[11px] text-muted-foreground" data-testid="out-zone-via">
                    via {shipmentZone.matched_via?.replace(/_/g, " ")}
                  </span>
                </>
              ) : (
                <span className="text-[11px] text-muted-foreground" data-testid="out-zone-empty">
                  {zoneLoading ? "Resolving…" :
                    !form.courier ? "Pick a courier to auto-resolve"
                                  : !form.destination_pincode ? "Enter destination pincode"
                                  : "Awaiting inputs…"}
                </span>
              )}
              <span className="ml-auto text-[10px] text-muted-foreground">Auto-calculated · Read-only</span>
            </div>
            <RateSuggestion
              pincode={form.destination_pincode}
              source_pincode={form.source_pincode}
              weight_kg={form.weight_kg}
              mode={form.shipping_mode}
              urgency={urgency}
              service_type={currentServiceType}
              selectedCourier={form.courier}
              selectedService={currentServiceType}
              onPick={(c, svc) => {
                set("courier", c);
                // Independent Standard vs Express selection: also switch the
                // Ship Product so the Rate Matrix service_type stays in sync.
                const sp = SERVICE_TO_SHIP_PRODUCT[svc];
                if (sp) set("ship_product", sp);
              }}
            />
          </div>

          <div className="border-t border-border pt-6 space-y-4">
            <div className="overline">Shipping mode & tracking</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="overline">Mode</span>
                <select data-testid="out-entry-mode" value={form.entry_mode}
                  onChange={(e) => set("entry_mode", e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
                  <option value="Courier">Courier</option>
                  <option value="Postal">Postal</option>
                  <option value="Intercompany">Intercompany</option>
                </select>
              </div>
              <div>
                <span className="overline">Scope</span>
                <select data-testid="out-scope" value={form.scope}
                  onChange={(e) => set("scope", e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
                  <option value="Domestic">Domestic</option>
                  <option value="International">International</option>
                </select>
              </div>
              <div>
                <span className="overline">Ship Product <span className="text-primary">*</span></span>
                <select
                  data-testid="out-ship-product"
                  value={form.ship_product || "Surface"}
                  onChange={(e) => set("ship_product", e.target.value)}
                  required
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                >
                  <option value="Air Doc">Air Doc</option>
                  <option value="Air Non-Doc">Air Non-Doc</option>
                  <option value="Surface">Surface</option>
                  <option value="Cargo">Cargo</option>
                </select>
              </div>
            </div>
            {form.scope === "International" && (
              <div className="border border-primary/40 bg-red-50/50 rounded-md p-3" data-testid="out-customs-wrap">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="overline flex items-center gap-1.5 text-primary">
                      Customs value (INR)<span className="text-primary">*</span>
                    </span>
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-sm text-muted-foreground">₹</span>
                      <Input
                        data-testid="out-customs-value"
                        type="number" step="0.01" min="0"
                        value={form.customs_value_inr ?? ""}
                        onChange={(e) => set("customs_value_inr", e.target.value === "" ? null : parseFloat(e.target.value))}
                        placeholder="Declared value for customs · e.g. 12500"
                        className="rounded-md border-input focus-visible:ring-primary font-mono"
                        required
                      />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Mandatory for cross-border shipments. Used on commercial invoice + customs declaration.
                    </div>
                  </label>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="overline">Internal Reference ID</span>
                <span className="ml-1 text-[9px] font-bold text-primary bg-red-50 border border-primary px-1.5 py-0.5 rounded">AUTO</span>
                <input readOnly value="MR-{TENANT_CODE}-{YYMMDD}-#### (assigned on save)"
                  data-testid="out-reference-preview"
                  className="mt-1 w-full h-10 px-3 rounded-md border border-dashed border-primary/40 bg-red-50/40 text-xs font-mono text-primary/80 cursor-not-allowed" />
                <div className="text-[10px] text-muted-foreground mt-1">Generated automatically on submit.</div>
              </div>
              <div>
                <span className="overline">AWB / Consignment # <span className="text-primary">*</span></span>
                <div className="mt-1 flex gap-2">
                  <input value={form.awb_number} onChange={(e) => set("awb_number", e.target.value)}
                    data-testid="out-awb"
                    placeholder="Scan label or type AWB #"
                    className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm font-mono focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
                  <input ref={awbOcrRef} type="file" accept="image/*" capture="environment"
                    data-testid="out-awb-ocr-input" hidden
                    onChange={(e) => handleAwbOcr(e.target.files?.[0])} />
                  <button type="button" data-testid="out-awb-ocr-btn"
                    disabled={awbOcrBusy}
                    onClick={() => awbOcrRef.current?.click()}
                    title="Snap or upload a photo of the label — AI extracts the AWB number"
                    className="h-10 px-3 rounded-md border border-primary/50 bg-primary/5 text-primary font-semibold text-xs inline-flex items-center gap-1.5 hover:bg-primary hover:text-white transition-colors disabled:opacity-50">
                    <Camera size={13}/> {awbOcrBusy ? "Reading…" : "Snap"}
                  </button>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Required. Snap the courier's label or type it in manually.</div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6 space-y-4">
            <div className="overline">Parcel</div>
            <WeightBlock form={form} set={set} prefix="out" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <F label="# Items" mono type="number" step="1" v={form.item_count} set={(v)=>set("item_count", Math.max(1, parseInt(v)||1))} t="out-item-count"/>
              <F label="Dimensions (freeform)" v={form.dimensions} set={(v)=>set("dimensions",v)} t="out-dims"/>
              <div>
                <span className="overline">Type</span>
                <ParcelTypeSelect value={form.parcel_type} onChange={(v)=>set("parcel_type",v)} testid="out-type" />
              </div>
              <div>
                <span className="overline">Courier chosen</span>
                <CourierSelect value={form.courier} onChange={(v) => set("courier", v)} testid="out-courier" />
              </div>
            </div>
            <ShipmentTypeRadio value={form.shipment_type} onChange={(v)=>set("shipment_type",v)} prefix="out" />
            <ItemBreakdownEditor
              value={form.items}
              onChange={(items) => {
                const totalCount = items && items.length ? items.reduce((n,r) => n + (Number(r.count)||0), 0) : null;
                const totalWeight = items && items.length ? items.reduce((n,r) => n + ((Number(r.weight_kg)||0) * (Number(r.count)||0)), 0) : 0;
                setForm(s => ({
                  ...s,
                  items,
                  item_count: totalCount || s.item_count,
                  weight_kg: totalWeight > 0 ? Math.round(totalWeight * 1000) / 1000 : s.weight_kg,
                }));
              }}
              prefix="out-items"
            />
          </div>

          <div className="border-t border-border pt-6 space-y-4">
            <ParcelConditionSection
              value={form.condition}
              onChange={(c) => set("condition", c)}
              prefix="out-cond"
            />
          </div>

          {/* Cost Center — mandatory for outward parcels. Lists only ACTIVE centers. */}
          <div className="border-t border-border pt-6" data-testid="out-cost-center-wrap">
            <CostCenterSelect
              value={form.cost_center_id}
              onChange={(id) => set("cost_center_id", id)}
              required
            />
          </div>

          <div className="border-t border-border pt-6 space-y-3">
            <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${form.priority ? "border-primary bg-red-50" : "border-border hover:border-primary/40"}`} data-testid="out-priority-toggle-wrap">
              <input
                type="checkbox"
                data-testid="out-priority-toggle"
                checked={!!form.priority}
                onChange={(e) => set("priority", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#BE1E1E] cursor-pointer"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <Zap size={14} className={form.priority ? "text-primary" : "text-muted-foreground"} />
                  Mark as Priority
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Optional</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Notifies admin + recipient every 4h until dispatched/delivered. Rate is entered manually alongside the courier below.
                </div>
              </div>
            </label>

            {form.priority && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-1" data-testid="priority-manual-fields">
                <div>
                  <span className="overline">Courier (manual)<span className="text-primary"> *</span></span>
                  <CourierSelect value={form.courier} onChange={(v) => set("courier", v)} testid="out-courier-manual" />
                  <div className="text-[10px] text-muted-foreground mt-1">Select the courier you will actually book with.</div>
                </div>
                <div>
                  <span className="overline">Rate ₹ (manual)<span className="text-primary"> *</span></span>
                  <Input
                    data-testid="out-priority-rate"
                    type="number" step="0.01" min="0"
                    value={form.priority_rate ?? ""}
                    onChange={(e) => set("priority_rate", e.target.value === "" ? null : parseFloat(e.target.value))}
                    className="mt-1 rounded-md border-input focus-visible:ring-primary font-mono"
                    placeholder="e.g. 249.00"
                    required
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">Overrides the auto rate compare for this priority booking.</div>
                </div>
              </div>
            )}

            {/* T3 · Return-To-Office */}
            <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${form.is_rto ? "border-primary bg-red-50/60" : "border-border hover:border-primary/40"}`} data-testid="out-rto-toggle-wrap">
              <input
                type="checkbox"
                data-testid="out-rto-toggle"
                checked={!!form.is_rto}
                onChange={(e) => set("is_rto", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#BE1E1E] cursor-pointer"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">Return To Office (RTO)</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  On save, an inward parcel is auto-created and linked back to this outward. Checkbox is locked once RTO is processed.
                </div>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2 items-center flex-wrap">
            <label className="flex items-center gap-2 text-sm mr-auto cursor-pointer" data-testid="out-book-immediately-wrap">
              <input
                type="checkbox"
                data-testid="out-book-immediately"
                checked={bookImmediately}
                onChange={(e) => setBookImmediately(e.target.checked)}
                className="h-4 w-4 accent-[#BE1E1E]"
              />
              <span className="font-semibold">Book Parcel Immediately</span>
              <span className="text-[11px] text-muted-foreground">Opens the Booking window right after save</span>
            </label>
            <Button data-testid="out-submit" type="submit" disabled={saving}
              className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              {saving ? "Booking…" : "Book Outward →"}
            </Button>
          </div>
        </form>

        {/* Rate compare + AI */}
        <div className="lg:col-span-2 space-y-4">
          <CostBreakdownPanel
            netAmount={netAmount}
            fuelSurcharge={form.fuel_surcharge}
            onFuelChange={(v) => set("fuel_surcharge", v)}
            testid="out-cost-breakdown"
          />

          <div className="border border-border bg-card p-6" data-testid="out-ai-panel">
            <div className="flex items-center justify-between">
              <div className="overline flex items-center gap-2"><Sparkles size={12} className="text-accent"/> AI Advisor</div>
              <Button data-testid="ai-recommend-btn" type="button" onClick={askAI} disabled={aiLoading}
                className="rounded-md bg-foreground text-background hover-brut shadow-brut">
                {aiLoading ? "Thinking…" : "Recommend"}
              </Button>
            </div>
            {ai ? (
              <div className="mt-4 space-y-3">
                <div>
                  <div className="overline">Recommended</div>
                  <div className="font-display text-3xl text-primary">{ai.recommendation}</div>
                </div>
                <div className="text-sm">{ai.reason}</div>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <div className="border border-border p-2">
                    <div className="overline">ETA</div>
                    <div className="mono">{ai.estimated_days}d</div>
                  </div>
                  <div className="border border-border p-2">
                    <div className="overline">Alt.</div>
                    <div className="mono text-xs">{(ai.alternatives||[]).join(", ")}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 text-sm text-muted-foreground">Tap Recommend for Gemini-powered courier reasoning based on weight, city, urgency.</div>
            )}
          </div>
        </div>
      </div>

      {/* Book-Parcel dialog: opens automatically when "Book Parcel Immediately" was ticked at save. */}
      <BookParcelDialog
        open={bookOpen}
        selectedParcels={savedParcel ? [savedParcel] : []}
        onClose={() => {
          // User cancelled the booking. The parcel is already saved with booking_status=not_booked;
          // navigate to the parcel list so it's visible for later booking.
          setBookOpen(false);
          setSavedParcel(null);
          setBookImmediately(false);
          setForm(empty);
          nav("/app/parcels?direction=outward");
        }}
        onBooked={() => {
          // Booking succeeded — head to the parcel list where SLA countdown is now visible.
          setBookOpen(false);
          setSavedParcel(null);
          setBookImmediately(false);
          setForm(empty);
          nav("/app/parcels?direction=outward");
        }}
      />

      {/* AWB-missing prompt (Feb 12, 2026) — blocks save until an AWB is provided. */}
      <Dialog open={awbPromptOpen} onOpenChange={setAwbPromptOpen}>
        <DialogContent className="rounded-md max-w-md" data-testid="out-awb-prompt">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <ScanLine size={18} className="text-primary"/> AWB not entered
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Scan the courier label or type the AWB manually. Save is blocked until an AWB is present.</p>
            <button type="button" data-testid="out-awb-prompt-scan"
              onClick={() => awbOcrRef.current?.click()}
              disabled={awbOcrBusy}
              className="w-full h-11 rounded-md border border-primary/50 bg-primary/5 text-primary font-semibold inline-flex items-center justify-center gap-2 hover:bg-primary hover:text-white transition-colors disabled:opacity-50">
              <Camera size={14}/> {awbOcrBusy ? "Reading…" : "Open scanner / camera"}
            </button>
            <div className="text-[11px] uppercase tracking-widest text-center text-muted-foreground">or</div>
            <label className="block">
              <span className="overline">Type AWB manually</span>
              <Input data-testid="out-awb-prompt-input" value={awbManual}
                onChange={(e) => setAwbManual(e.target.value)}
                placeholder="e.g. BD1234567890"
                className="mt-1 rounded-md font-mono"/>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAwbPromptOpen(false)} className="rounded-md"
                data-testid="out-awb-prompt-cancel">Cancel</Button>
              <Button
                onClick={() => {
                  const v = awbManual.trim();
                  if (!v) { toast.error("Enter an AWB or use the scanner"); return; }
                  set("awb_number", v);
                  setAwbPromptOpen(false);
                  toast.success(`AWB set · ${v}. Click Book Outward again to save.`);
                }}
                className="rounded-md bg-primary text-primary-foreground"
                data-testid="out-awb-prompt-save">Use AWB</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, set, type="text", required, mono, textarea, t, step }) {
  const cn = `mt-1 rounded-md border-input focus-visible:ring-primary ${mono?"font-mono":""}`;
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      {textarea
        ? <textarea data-testid={t} rows={2} value={v||""} onChange={(e)=>set(e.target.value)} className={`w-full ${cn} border p-2 text-sm bg-background`} />
        : <Input data-testid={t} type={type} step={step} value={v??""} onChange={(e)=>set(e.target.value)} required={required} className={cn} />
      }
    </label>
  );
}
